import React from 'react';

export interface StatusProps {
  value: string;
  type?: string;
  size?: 'small' | 'medium' | 'large';
  className?: string;
}

interface StatusConfig {
  label: string;
  bgColor: string;
  textColor: string;
}

// Status configurations for different types
const statusConfigs: Record<string, Record<string, StatusConfig>> = {
  contract: {
    RTO: {
      label: 'Rent to Own',
      bgColor: 'bg-feature-lighter',
      textColor: 'text-primary',
    },
    RENT: {
      label: 'Rent',
      bgColor: 'bg-warning-lighter',
      textColor: 'text-warning-base',
    },
  },

  feedback: {
    complete: {
      label: 'Complete',
      bgColor: 'bg-success-lighter',
      textColor: 'text-success-dark',
    },
    completed: {
      label: 'Completed',
      bgColor: 'bg-success-lighter',
      textColor: 'text-success-dark',
    },
    active: {
      label: 'Active',
      bgColor: 'bg-success-lighter',
      textColor: 'text-success-dark',
    },
    approved: {
      label: 'Approved',
      bgColor: 'bg-success-lighter',
      textColor: 'text-success-dark',
    },
    delivered: {
      label: 'Delivered',
      bgColor: 'bg-success-lighter',
      textColor: 'text-success-dark',
    },
    online: {
      label: 'Online',
      bgColor: 'bg-success-lighter',
      textColor: 'text-success-dark',
    },

    // Inactive/Not Active states
    inactive: {
      label: 'Not Active',
      bgColor: 'bg-bg-weak-50',
      textColor: 'text-text-sub-600',
    },
    'not active': {
      label: 'Not Active',
      bgColor: 'bg-bg-weak-50',
      textColor: 'text-text-sub-600',
    },

    // Warning states (yellow)
    'in delivery': {
      label: 'In Delivery',
      bgColor: 'bg-away-lighter',
      textColor: 'text-away-dark',
    },
    'in progress': {
      label: 'In Progress',
      bgColor: 'bg-away-lighter',
      textColor: 'text-away-dark',
    },
    'pending pickup': {
      label: 'Pending Pickup',
      bgColor: 'bg-away-lighter',
      textColor: 'text-away-dark',
    },
    'picking up': {
      label: 'Picking Up',
      bgColor: 'bg-away-lighter',
      textColor: 'text-away-dark',
    },
    processing: {
      label: 'Processing',
      bgColor: 'bg-away-lighter',
      textColor: 'text-away-dark',
    },

    // Error states
    failed: {
      label: 'Failed',
      bgColor: 'bg-error-lighter',
      textColor: 'text-error-dark',
    },
    error: {
      label: 'Error',
      bgColor: 'bg-error-lighter',
      textColor: 'text-error-dark',
    },
    rejected: {
      label: 'Rejected',
      bgColor: 'bg-error-lighter',
      textColor: 'text-error-dark',
    },
    offline: {
      label: 'Offline',
      bgColor: 'bg-error-lighter',
      textColor: 'text-error-dark',
    },
    unavailable: {
      label: 'Unavailable',
      bgColor: 'bg-error-lighter',
      textColor: 'text-error-dark',
    },

    // Info states
    allocating: {
      label: 'Allocating',
      bgColor: 'bg-information-lighter',
      textColor: 'text-information-dark',
    },
    queueing: {
      label: 'Queueing',
      bgColor: 'bg-information-lighter',
      textColor: 'text-information-dark',
    },
    scheduled: {
      label: 'Scheduled',
      bgColor: 'bg-information-lighter',
      textColor: 'text-information-dark',
    },
    reviewing: {
      label: 'Reviewing',
      bgColor: 'bg-information-lighter',
      textColor: 'text-information-dark',
    },

    // Neutral states
    canceled: {
      label: 'Canceled',
      bgColor: 'bg-bg-weak-50',
      textColor: 'text-text-sub-600',
    },
    cancelled: {
      label: 'Cancelled',
      bgColor: 'bg-bg-weak-50',
      textColor: 'text-text-sub-600',
    },
    draft: {
      label: 'Draft',
      bgColor: 'bg-bg-weak-50',
      textColor: 'text-text-sub-600',
    },
    idle: {
      label: 'Idle',
      bgColor: 'bg-away-lighter',
      textColor: 'text-away-base',
    },

    // Maintenance states
    maintenance: {
      label: 'Maintenance',
      bgColor: 'bg-feature-lighter',
      textColor: 'text-primary',
    },
    'in repair': {
      label: 'In Repair',
      bgColor: 'bg-feature-lighter',
      textColor: 'text-primary',
    },

    // Broken states
    broken: {
      label: 'Broken',
      bgColor: 'bg-error-lighter',
      textColor: 'text-error-base',
    },

    // Pending states (orange)
    pending: {
      label: 'Pending',
      bgColor: 'bg-warning-lighter',
      textColor: 'text-warning-dark',
    },
    waiting: {
      label: 'Waiting',
      bgColor: 'bg-warning-lighter',
      textColor: 'text-warning-dark',
    },
    hold: {
      label: 'Hold',
      bgColor: 'bg-warning-lighter',
      textColor: 'text-warning-dark',
    },
  },

  handover: {
    returned: {
      label: 'Returned',
      bgColor: 'bg-feature-lighter',
      textColor: 'text-primary',
    },
  },
};

export const Status: React.FC<StatusProps> = ({
  value,
  type = 'general',
  size = 'medium',
  className = '',
}) => {
  const typeConfigs = statusConfigs[type] || {};
  const config = typeConfigs[value] || typeConfigs[value.toLowerCase()];

  const fallbackConfig: StatusConfig = {
    label: value,
    bgColor: 'bg-bg-weak-50',
    textColor: 'text-text-sub-600',
  };

  const finalConfig = config || fallbackConfig;

  const sizeClasses = {
    small: 'px-2 py-0.5 text-xs',
    medium: 'px-2.5 py-0.5 text-xs',
    large: 'px-3 py-1 text-sm',
  };

  const baseClasses = 'inline-flex items-center font-medium rounded-full';

  const finalClasses =
    `${baseClasses} ${sizeClasses[size]} ${finalConfig.bgColor} ${finalConfig.textColor} ${className}`.trim();

  return <span className={finalClasses}>{finalConfig.label}</span>;
};

export default Status;
